// A date input with a calendar popover (revamp PR 5): the Lumen replacement for JarvisDateInput, with its rules
// kept exactly (date-field-logic.ts):
// - shows the value as DD-MM-YYYY and accepts it typed; any other text goes through the browser's date parser;
// - each keystroke that makes a valid date sets the value at once, other text leaves it as it was;
// - leaving the field puts the value's own text back; an empty field clears only with `clearable`;
// - a day picked in the calendar keeps the old value's time of day.
// The calendar opens on focus, click or typing and closes on Escape, blur, a pick or a click outside; focus stays
// in the input, as it did, so typing works while it is open. The keyboard path into it: ArrowDown (or Alt+ArrowDown)
// moves focus to the calendar's day (react-day-picker's roving day focus: the selected day, else today), where the
// arrow keys move and Enter or Space picks; a pick or Escape puts focus back in the input. The Lumen Calendar
// (react-day-picker) loads on first open, so a page that imports this eagerly doesn't pull it into the main chunk.
//
//   <DateField
//     value={startDate}                         // Date | null
//     onChange={setStartDate}                   // (Date | null) => void
//     placeholder={t('lumen.x.pickStartDate')}  // also the accessible name, unless aria-label is given
//     className="w-40"
//   />
//
// Optional: `id`, `aria-label`, `aria-describedby`, `invalid`, `required`, `disabled`, `clearable`, `clearLabel`
// (with `clearable`: Mantine's clear X and a click on the selected day clearing it; off by default), `minDate`,
// `maxDate`, `onBlur`, and `format` (`'DD-MM-YYYY'` default, or `'YYYY-MM-DD'`, JarvisDateInput's `valueFormat`): the
// display only, typing is read the same way in both (date-field-logic.ts). PR 4's shipment-quota/create has its own date-and-time field; it is not this one.
import { CalendarIcon, Loader2, X } from 'lucide-react';
import { lazy, Suspense, useRef, useState } from 'react';

import { controlClasses } from '@/components/lumen/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/lumen/popover';
import { cn } from '@/lib/utils';
import { calendarPick, type DateFieldFormat, formatDateField, showsClearButton, typedValue } from './date-field-logic';

const Calendar = lazy(() => import('@/components/lumen/calendar').then((module) => ({ default: module.Calendar })));

export interface DateFieldProps {
  value: Date | null;
  onChange: (value: Date | null) => void;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  invalid?: boolean;
  required?: boolean;
  disabled?: boolean;
  /** An emptied field clears the value (Mantine's `clearable`). Off by default, as on JarvisDateInput. */
  clearable?: boolean;
  /**
   * With `clearable`, as Mantine's DateInput did: an X on the right that empties the value (mouse-only, like
   * Mantine's: `tabIndex -1`), with this accessible name, and a click on the selected day in the calendar clears it.
   * Without it (the default) neither shows, as before.
   */
  clearLabel?: string;
  minDate?: Date;
  maxDate?: Date;
  /** The shown text: `DD-MM-YYYY` (default) or `YYYY-MM-DD`. Typing is parsed the same way in both. */
  format?: DateFieldFormat;
  /** The input lost focus (JarvisDateInput's `onBlur`, for "touched" validation). */
  onBlur?: () => void;
  /** On the wrapper: set the width here (`w-40`). */
  className?: string;
}

export function DateField({
  value,
  onChange,
  placeholder,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  invalid,
  required,
  disabled,
  clearable = false,
  clearLabel,
  minDate,
  maxDate,
  format = 'DD-MM-YYYY',
  onBlur,
  className,
}: DateFieldProps) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Set while focus comes back from the calendar, so the input's focus handler doesn't reopen it.
  const returningFocus = useRef(false);
  const [open, setOpen] = useState(false);
  // The text being typed; null shows the value's own text (Mantine's fixOnBlur).
  const [draft, setDraft] = useState<string | null>(null);
  const [month, setMonth] = useState<Date | undefined>(value ?? undefined);

  const show = () => {
    if (returningFocus.current) {
      returningFocus.current = false;
      return;
    }
    if (disabled || open) return;
    setMonth(value ?? undefined);
    setOpen(true);
  };

  // Back to the input from inside the calendar (a keyboard pick, Escape): close and refocus without reopening.
  const returnFocus = () => {
    if (!contentRef.current?.contains(document.activeElement)) return;
    returningFocus.current = true;
    inputRef.current?.focus();
  };

  // The calendar's focusable day: react-day-picker keeps one day button at tabIndex 0. The calendar chunk may still
  // be loading on the first open, so look for it over a few frames.
  const focusCalendar = (tries = 60) => {
    const day = contentRef.current?.querySelector<HTMLButtonElement>('[role="grid"] button[tabindex="0"]');
    if (day) day.focus();
    else if (tries > 0) requestAnimationFrame(() => focusCalendar(tries - 1));
  };

  const handleType = (text: string) => {
    setDraft(text);
    setOpen(true);
    const next = typedValue(text, { clearable, minDate, maxDate });
    if (next.kind === 'clear') onChange(null);
    if (next.kind === 'set') {
      onChange(next.date);
      setMonth(next.date);
    }
  };

  const deselect = Boolean(clearable && clearLabel);
  const clearButton = showsClearButton({ clearable, clearLabel, value, disabled });

  const pick = (day: Date | undefined) => {
    const next = calendarPick(value, day, deselect);
    if (next === undefined) return;
    onChange(next);
    setDraft(null);
    setOpen(false);
    returnFocus();
  };

  return (
    <Popover open={open} onOpenChange={(next) => !next && setOpen(false)}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} data-slot="date-field" className={cn('relative w-full', className)}>
          <CalendarIcon
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-fg-muted"
          />
          <input
            ref={inputRef}
            id={id}
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={draft ?? formatDateField(value, format)}
            placeholder={placeholder}
            aria-label={ariaLabel ?? placeholder}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-required={required || undefined}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled}
            onFocus={show}
            onClick={show}
            onChange={(event) => handleType(event.currentTarget.value)}
            onBlur={(event) => {
              setDraft(null);
              // Focus moving into the calendar (the keyboard path) keeps it open; anywhere else closes it.
              if (!contentRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false);
              onBlur?.();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setOpen(false);
              if (event.key === 'ArrowDown' && !disabled) {
                event.preventDefault();
                if (!open) {
                  setMonth(value ?? undefined);
                  setOpen(true);
                }
                focusCalendar();
              }
            }}
            className={cn(
              controlClasses,
              'h-9 pl-9 font-mono text-sm placeholder:font-ui',
              clearButton ? 'pr-9' : 'pr-3'
            )}
          />
          {clearButton && (
            <button
              type="button"
              tabIndex={-1}
              aria-label={clearLabel}
              className="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded-control text-fg-muted outline-none transition-colors hover:bg-tint hover:text-fg"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(null);
                setDraft(null);
                setOpen(false);
              }}
            >
              <X aria-hidden className="size-3.5" />
            </button>
          )}
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        className="w-auto p-0"
        // A calendar still on screen (its exit animation not yet ended) whose field was hidden, as on an engine tab
        // left for another, hides instead of landing at the page's top-left corner.
        hideWhenDetached
        // Focus stays in the input: typing keeps working while the calendar is open, and blur means "done".
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onMouseDown={(event) => event.preventDefault()}
        onEscapeKeyDown={returnFocus}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
      >
        <Suspense
          fallback={
            <div className="grid h-72 w-64 place-items-center">
              <Loader2 aria-hidden className="size-5 animate-spin text-fg-muted motion-reduce:animate-none" />
            </div>
          }
        >
          <Calendar
            mode="single"
            required={!deselect}
            selected={value ?? undefined}
            month={month}
            onMonthChange={setMonth}
            onSelect={pick}
            disabled={[...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])]}
          />
        </Suspense>
      </PopoverContent>
    </Popover>
  );
}
